import {
  Box,
  ChakraProps,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Slide,
  StackDivider,
} from "@chakra-ui/react";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";
import { RefObject, useState, useEffect } from "react";
import { BOX_RESIZE_DURATION_MS } from "../slideInMenu/constants";
import { motion } from "framer-motion";
import { Z_INDEX_MODAL } from "../../screens/root/constants";

interface GCModalProps {
  isOpen: boolean;
  onClose: () => void;
  header?: React.ReactNode;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  width?: ChakraProps["width"];
  minWidth?: ChakraProps["minWidth"];
  maxWidth?: ChakraProps["maxWidth"];
  height?: ChakraProps["height"];
  maxHeight?: ChakraProps["maxHeight"];
  minHeight?: ChakraProps["minHeight"];
  locked?: boolean;
  trapFocus?: boolean;
  dividerPadding?: string;
  hideDivider?: boolean;
  marginBottom?: ChakraProps["marginBottom"];
  enableSizeTransitions?: boolean;
  contentRef?: RefObject<HTMLDivElement>;
  zIndex?: number;
  /**
   * When true (and a footer is provided), the footer renders OUTSIDE the
   * scrollable body container — pinned to the modal's bottom edge. Default
   * false: the footer renders inside the scroll container, scrolling with
   * the body content (the original GCModal behavior; preserved for the
   * many existing callers that rely on auto-margin centering of body+footer
   * for short content).
   */
  stickyFooter?: boolean;
}

export function GCModal({
  isOpen,
  onClose,
  header,
  body,
  footer,
  width,
  minWidth,
  maxWidth,
  height,
  maxHeight,
  minHeight,
  locked = false,
  trapFocus = false,
  dividerPadding,
  hideDivider = false,
  marginBottom,
  enableSizeTransitions = false,
  contentRef,
  zIndex = Z_INDEX_MODAL,
  stickyFooter = false,
}: GCModalProps) {
  const isMobile = useMobileBreakpoint();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      motionPreset="none"
      size={isMobile ? "full" : undefined}
      closeOnEsc={!locked && !trapFocus}
      closeOnOverlayClick={!locked && !trapFocus}
      trapFocus={!locked || trapFocus}
      blockScrollOnMount={false}
    >
      <ModalOverlay
        cursor={trapFocus ? "default" : "pointer"}
        onClick={() => {
          if (trapFocus) return;
          onClose();
        }}
        pointerEvents={!locked ? "all" : "none"}
        zIndex={locked ? undefined : zIndex}
      />
      {isMobile ? (
        <Slide
          in={isOpen}
          direction="bottom"
          style={{ width: "100%", height: "100dvh", zIndex: zIndex }}
        >
          <ModalContentContent
            width="100%"
            header={header}
            body={body}
            footer={footer}
            stickyFooter={stickyFooter}
            borderRadius={"0px"}
            minWidth="100%"
            maxWidth="100%"
            height="100dvh"
            maxHeight="100dvh"
            minHeight={minHeight}
            locked={locked}
            dividerPadding={dividerPadding}
            hideDivider={hideDivider}
            marginBottom={marginBottom}
            enableSizeTransitions={false}
            contentRef={undefined}
            zIndex={zIndex}
          />
        </Slide>
      ) : (
        <ModalContentContent
          width={width ?? maxWidth}
          header={header}
          body={body}
          footer={footer}
          stickyFooter={stickyFooter}
          minWidth={minWidth}
          maxWidth={maxWidth}
          height={height}
          maxHeight={maxHeight || "calc(90vh - 32px)"}
          minHeight={minHeight}
          locked={locked}
          dividerPadding={dividerPadding}
          hideDivider={hideDivider}
          marginBottom={marginBottom}
          enableSizeTransitions={enableSizeTransitions}
          contentRef={contentRef}
          zIndex={zIndex}
        />
      )}
    </Modal>
  );
}

interface ModalContentContentProps {
  width?: ChakraProps["width"];
  header?: React.ReactNode;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  stickyFooter?: boolean;
  borderRadius?: string;
  minWidth?: ChakraProps["minWidth"];
  maxWidth?: ChakraProps["maxWidth"];
  height?: ChakraProps["height"];
  minHeight?: ChakraProps["minHeight"];
  maxHeight?: ChakraProps["maxHeight"];
  locked?: boolean;
  dividerPadding?: string;
  hideDivider: boolean;
  marginBottom?: ChakraProps["marginBottom"];
  enableSizeTransitions?: boolean;
  contentRef?: RefObject<HTMLDivElement>;
  zIndex: number;
}

function ModalContentContent({
  width,
  header,
  body,
  footer,
  stickyFooter = false,
  borderRadius,
  minWidth,
  maxWidth,
  height,
  maxHeight,
  minHeight,
  locked,
  dividerPadding,
  hideDivider = false,
  marginBottom,
  enableSizeTransitions = false,
  contentRef,
  zIndex,
}: ModalContentContentProps) {
  const isMobile = useMobileBreakpoint();
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Track when height changes to temporarily hide overflow during transition
  useEffect(() => {
    if (!enableSizeTransitions || !height) return;

    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, BOX_RESIZE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [height, enableSizeTransitions]);

  const shouldHideOverflow = enableSizeTransitions && isTransitioning;

  return (
    <ModalContent
      as={motion.div}
      borderRadius={borderRadius || "16px"}
      width={width ?? maxWidth ?? "fit-content"}
      minWidth={minWidth}
      maxWidth={maxWidth}
      height={height}
      maxHeight={maxHeight || "90vh"}
      overflow="hidden"
      minHeight={minHeight}
      border={"1px solid var(--chakra-colors-transparent-white-10)"}
      background="#141517f2"
      alignSelf={isMobile ? undefined : "center"}
      backdropFilter={"blur(18px)"}
      boxShadow={"0px 4px 24px 0px var(--chakra-colors-transparent-black-40)"}
      containerProps={{
        pointerEvents: "none",
        zIndex: locked ? undefined : zIndex,
        overflow: shouldHideOverflow ? "hidden" : "unset",
        maxWidth: "100%",
      }}
      sx={
        enableSizeTransitions && height
          ? {
              transition: `height ${BOX_RESIZE_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            }
          : undefined
      }
    >
      <ModalBody
        padding="0px"
        pointerEvents="auto"
        borderRadius={borderRadius ?? "16px"}
        maxHeight={maxHeight}
      >
        <Flex
          id="gcModalContentContainer"
          ref={contentRef}
          direction="column"
          borderRadius={borderRadius ?? "16px"}
          background={hideDivider ? "#141517f2" : undefined}
          height={isMobile ? "100dvh" : undefined}
          maxHeight={maxHeight}
        >
          {header && (
            <>
              <Box
                background="#141517f2"
                borderTopRadius={borderRadius ?? "16px"}
              >
                {header}
              </Box>
              {!locked && !hideDivider && (
                <StackDivider
                  height="1px"
                  backgroundColor="transparent.white.10"
                />
              )}
            </>
          )}
          <Flex
            id="gcModalScrollContainer"
            className="styled-scrollbar"
            direction="column"
            background="#141517f2"
            overflowY="auto"
            overscrollBehavior="contain"
            borderRadius={borderRadius ?? "16px"}
            borderTopRadius={header ? "0px" : (borderRadius ?? "16px")}
            flexDirection="column"
            flex="1"
            minHeight={0}
            marginBottom={marginBottom}
          >
            <Box
              margin={hideDivider ? "auto" : " auto 0"}
              width={hideDivider ? "100%" : undefined}
            >
              <Box borderRadius={borderRadius ?? "16px"}>{body}</Box>
              {footer && !stickyFooter && (
                <>
                  <StackDivider
                    height="1px"
                    backgroundColor="transparent.white.10"
                    mx={dividerPadding}
                  />
                  <Box>{footer}</Box>
                </>
              )}
            </Box>
          </Flex>
          {footer && stickyFooter && (
            <>
              <StackDivider
                height="1px"
                backgroundColor="transparent.white.10"
                mx={dividerPadding}
              />
              <Box
                background="#141517f2"
                borderBottomRadius={borderRadius ?? "16px"}
                flexShrink={0}
              >
                {footer}
              </Box>
            </>
          )}
        </Flex>
      </ModalBody>
    </ModalContent>
  );
}
